// =============================================
// usePipelineConfig — read/write batch config per pipeline step
// =============================================

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface StepConfig {
  step: string;
  batch_size: number;
  parallel_batches: number;
}

const DEFAULT_CONFIGS: StepConfig[] = [
  { step: "entity_extraction", batch_size: 50, parallel_batches: 3 },
  { step: "eav_extraction", batch_size: 20, parallel_batches: 3 },
  { step: "linking", batch_size: 30, parallel_batches: 3 },
  { step: "content_engine", batch_size: 1, parallel_batches: 3 },
  { step: "seed_generation", batch_size: 8, parallel_batches: 1 },
  { step: "node_generation", batch_size: 20, parallel_batches: 1 },
];

const STEP_LABELS: Record<string, string> = {
  entity_extraction: "Encje — ekstrakcja",
  eav_extraction: "EAV — atrybuty encji",
  linking: "Relacje — linkowanie",
  content_engine: "Content Engine — artykuły",
  seed_generation: "Seeds — klastry",
  node_generation: "Nodes — artykuły per seed",
};

export function usePipelineConfig() {
  const [configs, setConfigs] = useState<StepConfig[]>(DEFAULT_CONFIGS);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchConfigs = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from("pipeline_config")
        .select("step, batch_size, parallel_batches")
        .eq("user_id", user.id);

      if (error) {
        // Table may not exist yet — use defaults
        setConfigs(DEFAULT_CONFIGS);
        setLoading(false);
        return;
      }

      if (data && data.length > 0) {
        // Merge DB values with defaults (for any missing steps)
        const merged = DEFAULT_CONFIGS.map((def) => {
          const dbVal = (data as StepConfig[]).find((d) => d.step === def.step);
          return dbVal || def;
        });
        setConfigs(merged);
      }
    } catch {
      // Table doesn't exist — use defaults silently
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const getConfig = useCallback(
    (step: string): StepConfig => {
      return configs.find((c) => c.step === step) || { step, batch_size: 50, parallel_batches: 3 };
    },
    [configs],
  );

  const updateConfig = useCallback(
    async (step: string, batchSize: number, parallelBatches: number) => {
      if (!user) return;

      const clamped = {
        batch_size: Math.max(5, Math.min(200, batchSize)),
        parallel_batches: Math.max(1, Math.min(10, parallelBatches)),
      };

      try {
        await (supabase as any).from("pipeline_config").upsert(
          {
            user_id: user.id,
            step,
            ...clamped,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,step" },
        );

        setConfigs((prev) =>
          prev.map((c) => (c.step === step ? { ...c, ...clamped } : c)),
        );

        toast({ title: "Zapisano", description: `Konfiguracja ${STEP_LABELS[step] || step} zaktualizowana.` });
      } catch (err) {
        toast({
          title: "Błąd",
          description: err instanceof Error ? err.message : "Nie udało się zapisać",
          variant: "destructive",
        });
      }
    },
    [user, toast],
  );

  return {
    configs,
    loading,
    getConfig,
    updateConfig,
    stepLabels: STEP_LABELS,
    refresh: fetchConfigs,
  };
}
